import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import {
  concatMap,
  debounceTime,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  selectManureSpaceDetailsByManureCodeId,
  selectManureSpaceDetailsByPeriodId,
  selectManureSpaceDetailsId,
} from 'src/app/manure-space/store/manure-space.selectors';
import { CropFieldService } from 'src/app/shared/services/crop-field.service';
import * as SoilTypeSelectors from 'src/app/soil-type/store/soil-type.selectors';
import { getCrops, getCropYear } from 'src/app/store/selectors';

import * as FieldsActions from './actions';

import { getCropFields } from './selectors';

import * as LegalStandardActions from '../../fertilization-legal-standards/store/fertilization-legal-standard.actions';
import * as ManureSpaceActions from '../../manure-space/store/manure-space.actions';
import * as AppActionTypes from '../../store/actions';
import { FieldsQuickEditComponent } from '../fields-quick-edit/fields-quick-edit.component';
import { ManureSpaceSettingsService } from '../services/manure-space-settings.service';

@Injectable()
export class FieldsEffects {
  protected store = inject<Store<SiteState>>(Store);
  protected actions$ = inject(Actions);
  protected readonly dialog = inject(DialogService);
  private readonly fieldService = inject(CropFieldService);
  private readonly manureSpaceSettingsService = inject(ManureSpaceSettingsService);
  private readonly locale = inject(LocaleService);

  postManureSpaceSettingSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FieldsActions.postManureSpaceSettingSuccess),
      debounceTime(500),
      withLatestFrom(this.store.select(selectManureSpaceDetailsId)),
      map(([_, manureSpaceId]) => FieldsActions.getManureSpaceSettingsRequest({ manureSpaceId })),
    ),
  );

  setManureSpace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManureSpaceActions.setManureSpaceSuccess),
      withLatestFrom(this.store.select(selectManureSpaceDetailsId)),
      map(([_, manureSpaceId]) => FieldsActions.getManureSpaceSettingsRequest({ manureSpaceId })),
    ),
  );

  setManureSpaceByCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManureSpaceActions.setManureSpaceFilteredByManureCodeSuccess),
      withLatestFrom(this.store.select(selectManureSpaceDetailsByManureCodeId)),
      map(([_, manureSpaceId]) => FieldsActions.getManureSpaceSettingsRequest({ manureSpaceId })),
    ),
  );

  setManureSpaceByPeriod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManureSpaceActions.setManureSpaceByPeriodSuccess),
      withLatestFrom(this.store.select(selectManureSpaceDetailsByPeriodId)),
      map(([_, manureSpaceId]) => FieldsActions.getManureSpaceSettingsRequest({ manureSpaceId })),
    ),
  );

  getManureSpaceSettingsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FieldsActions.getManureSpaceSettingsSuccess),
      map((_) => LegalStandardActions.getFertilizationLegalStandards()),
    ),
  );

  getManureSpaceSettingsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FieldsActions.getManureSpaceSettingsRequest),
      mergeMap(({ manureSpaceId }) =>
        manureSpaceId
          ? this.manureSpaceSettingsService
              .getSettings(manureSpaceId)
              .pipe(map((settings) => FieldsActions.getManureSpaceSettingsSuccess({ settings })))
          : EMPTY,
      ),
    ),
  );

  getManureSpaceSettingTogglesRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FieldsActions.getManureSpaceSettingTogglesRequest),
      withLatestFrom(this.store.select(getCropYear), this.store.select(getCropFields)),
      mergeMap(([_, cropYear, cropFields]) => {
        const manureSpaceSettingTogglesRequestData = cropFields.map((cropField) => ({
          id: cropField.id,
          soilTypeId: cropField.soilTypeId,
          cropId: cropField.cropId,
          cropVarietyId: cropField.cropVarietyId,
          cropYear,
        }));
        return this.manureSpaceSettingsService
          .getSettingToggles(manureSpaceSettingTogglesRequestData)
          .pipe(
            map(({ settingToggles }) =>
              FieldsActions.getManureSpaceSettingTogglesSuccess({ settingToggles }),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
      }),
    ),
  );

  postManureSpaceSettingRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FieldsActions.postManureSpaceSettingRequest),
      withLatestFrom(this.store.select(selectManureSpaceDetailsId)),
      mergeMap(([{ manureSpaceSettingsData }, manureSpaceId]) =>
        this.manureSpaceSettingsService.postSetting(manureSpaceSettingsData, manureSpaceId).pipe(
          map(() => FieldsActions.postManureSpaceSettingSuccess()),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  openFieldsDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FieldsActions.openFieldsDialog),
        withLatestFrom(
          this.store.select(getCropFields),
          this.store.select(getCrops),
          this.store.select(SoilTypeSelectors.selectSoilTypes),
          this.store.select(getCropYear),
        ),
        switchMap(([_, cropFields, crops, soilTypes, cropYear]) =>
          this.dialog
            .form(
              FieldsQuickEditComponent,
              {
                title: `${this.locale.translate('fields.title')} ${cropYear}`,
                cropFields,
                crops,
                soilTypes,
                cropYear,
              },
              { width: '1400px', maxWidth: '90vw' },
            )
            .pipe(
              switchMap(({ status, result }) => {
                if (status === DIALOG_RETURN_TYPES.confirm) {
                  result.manureSpaceSettings.forEach((manureSpaceSettingsData) => {
                    this.store.dispatch(
                      FieldsActions.postManureSpaceSettingRequest({ manureSpaceSettingsData }),
                    );
                  });
                }
                return EMPTY;
              }),
            ),
        ),
      ),
    { dispatch: false },
  );

  loadCropFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FieldsActions.loadCropFieldsRequest),
      withLatestFrom(this.store.select(getCropYear)),
      mergeMap(([_, cropYear]) =>
        cropYear
          ? this.fieldService.getCropFields(cropYear).pipe(
              map((cropFields) => FieldsActions.loadCropFieldsSuccess({ cropFields })),
              startWith(CoreActions.setLoading({ loading: true })),
              finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
            )
          : EMPTY,
      ),
    ),
  );

  loadCropFieldsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FieldsActions.loadCropFieldsSuccess),
      concatMap((_) => [
        FieldsActions.getManureSpaceSettingTogglesRequest(),
        AppActionTypes.GetLatestSoilAnalysisRequest(),
      ]),
    ),
  );
}
